import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useContracts, useDeleteContract, useShows } from '../api/hooks';
import type { ContractListItemView } from '../api/types';
import { Badge, Button, DataTable, Modal, TextInput, useToast } from '../components/DesignSystem';
import type { DataTableColumn } from '../components/DesignSystem';
import { PageHeader } from '../components/PageHeader';
import { errorMessage } from '../lib/errors';
import { formatDateTime } from '../lib/format';

type ModalState = { kind: 'none' } | { kind: 'discard'; contract: ContractListItemView };

/** Short unique row identity: the user-supplied number, else the id prefix. */
const referenceOf = (contract: ContractListItemView): string =>
  contract.contractNumber ?? contract.id.slice(0, 8).toUpperCase();

/** `/uploads-in-progress` — resume or discard a draft contract from the upload wizard. */
export function UploadsInProgressPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const contracts = useContracts({ status: 'draft', limit: 100 });
  const shows = useShows({ limit: 100 });
  const deleteContract = useDeleteContract();

  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<ModalState>({ kind: 'none' });

  const allRows = useMemo(() => contracts.data?.data ?? [], [contracts.data]);
  const showList = shows.data?.data ?? [];

  const showNameOf = (contract: ContractListItemView): string =>
    showList.find((show) => show.id === contract.showId)?.name ?? '—';

  const seasonNameOf = (contract: ContractListItemView): string =>
    showList
      .find((show) => show.id === contract.showId)
      ?.seasons.find((season) => season.id === contract.seasonId)?.seasonCode ?? '—';

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === '') {
      return allRows;
    }
    return allRows.filter((row) =>
      [row.title, row.id, referenceOf(row), showNameOf(row), seasonNameOf(row)]
        .join(' ')
        .toLowerCase()
        .includes(needle),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows, search, showList]);

  async function handleDiscard(contract: ContractListItemView) {
    try {
      await deleteContract.mutateAsync(contract.id);
      showToast('Upload discarded');
      setModal({ kind: 'none' });
    } catch (error) {
      showToast(errorMessage(error, 'Could not discard upload'), 'error');
    }
  }

  const columns: DataTableColumn<ContractListItemView>[] = [
    { key: 'reference', header: 'Reference', render: (row) => referenceOf(row) },
    { key: 'show', header: 'Show Name', render: (row) => showNameOf(row) },
    { key: 'season', header: 'Season', render: (row) => seasonNameOf(row) },
    {
      key: 'type',
      header: 'Type',
      render: (row) => (
        <Badge tone={row.contractType === 'primary' ? 'primary' : 'teal'}>{row.contractType}</Badge>
      ),
    },
    {
      key: 'progress',
      header: 'Progress',
      render: (row) => (row.showId === null ? 'Needs show info' : 'In progress'),
    },
    { key: 'uploadedBy', header: 'Uploaded By', render: (row) => row.createdByName ?? '—' },
    { key: 'created', header: 'Created', render: (row) => formatDateTime(row.createdAt) },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (row) => (
        <div className="rfp-row-actions">
          <Button onClick={() => navigate(`/upload/${row.id}`)} type="button">
            Resume
          </Button>
          <Button
            onClick={() => setModal({ kind: 'discard', contract: row })}
            type="button"
            variant="secondary"
          >
            Discard
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        actions={
          <Button onClick={() => navigate('/upload')} type="button">
            Upload Contract
          </Button>
        }
        caption="Contracts started in the upload wizard but not yet authorised."
        title="Uploads in Progress"
      />

      <div className="rfp-toolbar">
        <div className="rfp-toolbar__search">
          <TextInput
            aria-label="Search uploads in progress"
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by show name, season, or ID..."
            value={search}
          />
        </div>
      </div>

      <section className="rfp-card">
        <div className="rfp-card__head">
          <h2 className="rfp-card__title">Uploads in Progress ({filteredRows.length})</h2>
        </div>
        {contracts.isLoading ? (
          <p className="rfp-loading">Loading uploads…</p>
        ) : contracts.isError ? (
          <p className="rfp-inline-error" role="alert">
            Could not load uploads — please retry.
          </p>
        ) : (
          <DataTable
            columns={columns}
            emptyMessage="No uploads in progress — start one from Upload Contract"
            rowKey={(row) => row.id}
            rows={filteredRows}
          />
        )}
      </section>

      {modal.kind === 'discard' && (
        <Modal
          actions={
            <>
              <Button onClick={() => handleDiscard(modal.contract)} type="button">
                Discard
              </Button>
              <Button onClick={() => setModal({ kind: 'none' })} type="button" variant="secondary">
                Cancel
              </Button>
            </>
          }
          title="Discard this upload?"
        >
          <p>
            “{modal.contract.title}” and any shares, residuals, or rights entered so far will be
            removed. This cannot be undone.
          </p>
        </Modal>
      )}
    </>
  );
}
